import { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import Loader from "../../shared/Loader";
import {
  prepareForfeitureAI,
  getForfeitureAIStatus,
  correctForfeitureAI,
  generateForfeitureJSON,
  PiiSpan,
  RedactResult,
} from "../../api/forfeitureAI";
import { FileDropzone } from "../../components/shared/FileDropzone";
import {
  ForfeitureSettings,
  ExerciseWindow,
  ExerciseWindowUnit,
  ExpiryMode,
  EmployeeExitType,
  ActiveEmployeeExpiry,
} from "../../types/EsopPlan";

const ALLOWED_LABELS_MAP: Record<string, string> = {
  private_person: "Private Person",
  private_email: "Private Email",
  private_phone: "Private Phone",
  private_address: "Private Address",
  private_date: "Private Date",
  private_url: "Private URL",
  account_number: "Account Number",
  secret: "Secret / Confidential",
  company_name: "Company Name",
  company_registration_number: "Company Reg No",
  company_address: "Company Address",
  company_email: "Company Email",
  company_phone: "Company Phone",
  company_url: "Company URL",
  company_bank_account: "Company Bank A/C",
  company_gst_pan: "Company GST/PAN",
};

function normalizeConditionalRules(
  settings: ForfeitureSettings
): ForfeitureSettings {
  return {
    ...settings,
    exitRules: settings.exitRules.map((rule) => {
      if (!rule.conditionalRules?.length) return rule;
      const sorted = [...rule.conditionalRules].sort(
        (a, b) => (a.tenure?.minMonths ?? 0) - (b.tenure?.minMonths ?? 0)
      );
      for (let i = 0; i < sorted.length - 1; i++) {
        const curr = sorted[i];
        const next = sorted[i + 1];
        if (
          curr.tenure?.maxMonths !== undefined &&
          next.tenure?.minMonths !== undefined &&
          next.tenure.minMonths === curr.tenure.maxMonths + 1
        ) {
          sorted[i] = {
            ...curr,
            tenure: { ...curr.tenure, maxMonths: next.tenure.minMonths },
          };
        }
      }
      return { ...rule, conditionalRules: sorted };
    }),
  };
}

function fmtWindow(ew: ExerciseWindow): string {
  if (ew.neverExpires) return "Never expires";
  const unitStr =
    ew.unit === ExerciseWindowUnit.DAYS
      ? "day"
      : ew.unit === ExerciseWindowUnit.MONTHS
      ? "month"
      : "year";
  return `${ew.value} ${unitStr}${ew.value !== 1 ? "s" : ""}`;
}

function fmtTenureBand(t: { minMonths: number; maxMonths?: number }): string {
  const parts = fmtTenureBandParts(t);
  return parts.hint ? `${parts.main} (${parts.hint})` : parts.main;
}

function fmtTenureBandParts(t: { minMonths: number; maxMonths?: number }): {
  main: string;
  hint: string | null;
} {
  const toYearsStr = (m: number) => {
    if (m % 12 === 0) {
      const y = m / 12;
      return `${y} year${y !== 1 ? "s" : ""}`;
    }
    return null;
  };
  if (t.minMonths === 0 && t.maxMonths !== undefined) {
    const yr = toYearsStr(t.maxMonths);
    return {
      main: `Less than ${t.maxMonths} months`,
      hint: yr ? `< ${yr}` : null,
    };
  }
  if (t.maxMonths === undefined) {
    const yr = toYearsStr(t.minMonths);
    return {
      main: `${t.minMonths}+ months`,
      hint: yr ? `${yr} or more` : null,
    };
  }
  return { main: `${t.minMonths}–${t.maxMonths} months`, hint: null };
}

function exitTypeIcon(type: EmployeeExitType): string {
  const map: Record<EmployeeExitType, string> = {
    [EmployeeExitType.RESIGNATION]: "mdi:briefcase-remove-outline",
    [EmployeeExitType.TERMINATION]: "mdi:account-remove-outline",
    [EmployeeExitType.RETIREMENT]: "mdi:human-cane",
    [EmployeeExitType.PERFORMANCE_RELATED]: "mdi:chart-line-variant",
    [EmployeeExitType.MISCONDUCT]: "mdi:alert-circle-outline",
    [EmployeeExitType.PERMANENT_DISABILITY]: "mdi:wheelchair-accessibility",
    [EmployeeExitType.DEATH]: "mdi:flower-outline",
    [EmployeeExitType.ABANDONMENT]: "mdi:run-fast",
  };
  return map[type] ?? "mdi:account-off-outline";
}

function fmtExitType(type: EmployeeExitType): string {
  const map: Record<EmployeeExitType, string> = {
    [EmployeeExitType.RESIGNATION]: "Resignation",
    [EmployeeExitType.TERMINATION]: "Termination",
    [EmployeeExitType.RETIREMENT]: "Retirement",
    [EmployeeExitType.PERFORMANCE_RELATED]: "Performance-Related Exit",
    [EmployeeExitType.MISCONDUCT]: "Misconduct",
    [EmployeeExitType.PERMANENT_DISABILITY]: "Permanent Disability",
    [EmployeeExitType.DEATH]: "Death",
    [EmployeeExitType.ABANDONMENT]: "Abandonment",
  };
  return map[type] ?? type;
}

function fmtActiveExpiry(ae: ActiveEmployeeExpiry): string {
  switch (ae.expiryMode) {
    case ExpiryMode.GRANT_EXPIRY:
      return "Options expire at the grant term end date";
    case ExpiryMode.NEVER:
      return "Options never expire for active employees";
    case ExpiryMode.FROM_VESTING_DATE:
      return `Options expire ${ae.expiryValue ?? ""} ${
        ae.expiryUnit
          ? ae.expiryUnit.charAt(0) + ae.expiryUnit.slice(1).toLowerCase()
          : "days"
      } after the vesting date`;
    case ExpiryMode.SPECIFIC_DATE:
      return `Options expire on ${ae.specificDate ?? "a specific date"}`;
    default:
      return "Unknown expiry mode";
  }
}

interface ForfeitureAIDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (json: ForfeitureSettings) => void;
}

type DialogStep = "input" | "polling" | "verify" | "generating" | "review";

interface GroupedSpan {
  placeholder_id: string;
  label: string;
  original_text: string;
  count: number;
}

export default function ForfeitureAIDialog({
  open,
  onClose,
  onApply,
}: ForfeitureAIDialogProps) {
  const [step, setStep] = useState<DialogStep>("input");
  const [loading, setLoading] = useState(false);
  const [inputType, setInputType] = useState<"text" | "file">("text");

  // Inputs
  const [textInput, setTextInput] = useState("");
  const [fileInput, setFileInput] = useState<File | null>(null);

  // Redaction job info
  const [jobId, setJobId] = useState<string | null>(null);
  const [redactResult, setRedactResult] = useState<RedactResult | null>(null);

  // Manual PII addition form
  const [addText, setAddText] = useState("");
  const [addLabel, setAddLabel] = useState("company_name");

  // Generated AI result (awaiting user confirmation)
  const [generatedJson, setGeneratedJson] = useState<ForfeitureSettings | null>(
    null
  );

  // Step 1b: Polling job status
  useEffect(() => {
    if (step !== "polling" || !jobId) return undefined;

    const interval = setInterval(async () => {
      try {
        const res = await getForfeitureAIStatus(jobId);
        if (res.status === "done") {
          setRedactResult({
            original_text: res.original_text,
            redacted_text: res.redacted_text,
            spans: res.spans,
          });
          setStep("verify");
        } else if (res.status === "failed") {
          toast.error(`Scanning failed: ${res.error || "Unknown error"}`);
          setStep("input");
        }
      } catch (err: any) {
        toast.error("Failed to check status. Retrying...");
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [step, jobId]);

  // Actions
  const handleStartAnalysis = async () => {
    if (inputType === "text" && !textInput.trim()) {
      toast.warn("Please paste some text to analyze.");
      return;
    }
    if (inputType === "file" && !fileInput) {
      toast.warn("Please select a DOCX file to analyze.");
      return;
    }

    setLoading(true);
    try {
      const payload =
        inputType === "text" ? { text: textInput } : { docx: fileInput! };
      const res = await prepareForfeitureAI(payload);
      setJobId(res.job_id);
      setStep("polling");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to start analysis"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSpan = async (placeholderId: string) => {
    if (!redactResult) return;
    setLoading(true);
    try {
      const updated = await correctForfeitureAI({
        original_text: redactResult.original_text,
        spans: redactResult.spans,
        remove: [placeholderId],
        add: [],
      });
      setRedactResult(updated);
      toast.success("PII marker removed");
    } catch (err: any) {
      toast.error("Failed to remove PII marker");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSpan = async () => {
    if (!redactResult || !addText.trim()) return;
    setLoading(true);
    try {
      const updated = await correctForfeitureAI({
        original_text: redactResult.original_text,
        spans: redactResult.spans,
        remove: [],
        add: [{ original_text: addText.trim(), label: addLabel }],
      });
      setRedactResult(updated);
      setAddText("");
      toast.success("PII marker added");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to add PII marker"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAndGenerate = async () => {
    if (!redactResult) return;
    setStep("generating");
    try {
      const res = await generateForfeitureJSON({
        redacted_text: redactResult.redacted_text,
      });
      const normalized = normalizeConditionalRules(res.forfeitureJson);
      setGeneratedJson(normalized);
      setStep("review");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "AI generation failed"
      );
      setStep("verify");
    }
  };

  // Group spans for easy viewing
  const groupedSpans = redactResult
    ? (() => {
        const map: Record<string, GroupedSpan> = {};
        for (const span of redactResult.spans) {
          if (!map[span.placeholder_id]) {
            map[span.placeholder_id] = {
              placeholder_id: span.placeholder_id,
              label: span.label,
              original_text: span.original_text,
              count: 0,
            };
          }
          map[span.placeholder_id].count++;
        }
        return Object.values(map);
      })()
    : [];

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <Box className="bg-white flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-8 border-b py-5 shrink-0 flex justify-between items-center">
          <HStack className="items-center gap-2">
            <Icon icon="mdi:sparkles" className="text-primary w-5 h-5" />
            <h6 className="text-base font-semibold text-gray-900">
              Generate Forfeiture Settings with AI
            </h6>
          </HStack>
          {!loading && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none p-1"
            >
              <Icon icon="mdi:close" className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body content */}
        <div className="flex-1 overflow-y-auto p-8">
          {step === "input" && (
            <VStack className="gap-6">
              {/* Type Switcher */}
              <div className="flex border border-gray-200 rounded-lg p-0.5 w-fit bg-gray-50 self-center">
                <button
                  type="button"
                  onClick={() => setInputType("text")}
                  className={`px-6 py-2 rounded-md text-xs font-semibold transition-all ${
                    inputType === "text"
                      ? "bg-white text-gray-800 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  📝 Paste Policy Text
                </button>
                <button
                  type="button"
                  onClick={() => setInputType("file")}
                  className={`px-6 py-2 rounded-md text-xs font-semibold transition-all ${
                    inputType === "file"
                      ? "bg-white text-gray-800 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  📄 Upload DOCX Document
                </button>
              </div>

              {inputType === "text" ? (
                <VStack className="gap-2">
                  <span className="text-xs font-semibold text-gray-700">
                    Paste Forfeiture/Expiry Policy Text
                  </span>
                  <textarea
                    rows={12}
                    placeholder="Paste the relevant sections from your ESOP scheme document regarding exit rules, forfeiture, retirement, vesting acceleration, exercise windows..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                  />
                  <span className="text-xs text-gray-400">
                    Note: All PII (names, dates, emails, company registration
                    details) will be automatically detected and redacted before
                    sending to the AI.
                  </span>
                </VStack>
              ) : (
                <VStack className="gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    Upload ESOP Scheme Document (.docx)
                  </span>
                  <FileDropzone
                    acceptedFileExtensions={[".docx"]}
                    instructionText=".docx files only"
                    selectedFile={fileInput ?? undefined}
                    onFileSelect={(f) => setFileInput(f)}
                    onClear={() => setFileInput(null)}
                  />
                  <span className="text-xs text-gray-400">
                    Plaintext extraction and PII redaction occur fully in-memory
                    on our servers.
                  </span>
                </VStack>
              )}
            </VStack>
          )}

          {step === "polling" && (
            <div className="py-12 flex flex-col items-center justify-center gap-4 text-center">
              <Loader />
              <VStack className="gap-1 mt-2">
                <span className="text-sm font-semibold text-gray-800">
                  Scanning document for PII...
                </span>
                <span className="text-xs text-gray-400 max-w-sm">
                  Detecting personal names, dates, contact details and company
                  details to redact. This takes about 10–30 seconds.
                </span>
              </VStack>
            </div>
          )}

          {step === "verify" && redactResult && (
            <VStack className="gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                <Icon
                  icon="mdi:shield-check-outline"
                  className="w-5 h-5 text-blue-600 shrink-0 mt-0.5"
                />
                <VStack className="gap-1">
                  <span className="text-xs font-semibold text-blue-800">
                    PII Redacted — Review Before Generating
                  </span>
                  <span className="text-xs text-blue-700">
                    Check the detected PII below. Mark anything as &quot;Not
                    PII&quot; to restore it, or add additional text to redact
                    before sending to the AI.
                  </span>
                </VStack>
              </div>

              {/* Text Preview Box */}
              <VStack className="gap-1.5">
                <span className="text-xs font-semibold text-gray-700">
                  Redacted Policy Preview
                </span>
                <div className="w-full border border-gray-200 bg-gray-50 rounded-lg p-4 text-xs font-mono text-gray-600 max-h-40 overflow-y-auto whitespace-pre-wrap">
                  {redactResult.redacted_text}
                </div>
              </VStack>

              {/* Spans list */}
              <VStack className="gap-3">
                <span className="text-xs font-semibold text-gray-700">
                  Detected PII & Company Identifiers ({groupedSpans.length})
                </span>
                {groupedSpans.length === 0 ? (
                  <div className="border border-dashed border-gray-200 rounded-lg py-8 text-center text-xs text-gray-400">
                    No PII detected. You can add items below if needed.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 max-h-60 overflow-y-auto">
                    {groupedSpans.map((span) => (
                      <HStack
                        key={span.placeholder_id}
                        className="justify-between items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <HStack className="items-center gap-3">
                          <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200">
                            {span.placeholder_id}
                          </span>
                          <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                            {ALLOWED_LABELS_MAP[span.label] || span.label}
                          </span>
                          <span className="text-xs text-gray-500 italic">
                            &quot;{span.original_text}&quot;
                          </span>
                          {span.count > 1 && (
                            <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.2 rounded-full font-semibold">
                              {span.count} occurrences
                            </span>
                          )}
                        </HStack>
                        <button
                          type="button"
                          disabled={loading}
                          onClick={() => handleRemoveSpan(span.placeholder_id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-2..5 py-1 rounded transition-all focus:outline-none"
                        >
                          <Icon icon="mdi:close" className="w-3.5 h-3.5" />
                          Not PII
                        </button>
                      </HStack>
                    ))}
                  </div>
                )}
              </VStack>

              {/* Manual Add Form */}
              <div className="border border-gray-100 rounded-lg bg-gray-50/50 p-4">
                <span className="text-xs font-semibold text-gray-700 block mb-3">
                  Manually Redact Additional PII
                </span>
                <HStack className="gap-3 items-end">
                  <VStack className="flex-1 gap-1">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                      Text to redact
                    </span>
                    <input
                      type="text"
                      placeholder="e.g. Alice Smith, 12/03/1990, ABC Limited"
                      value={addText}
                      onChange={(e) => setAddText(e.target.value)}
                      className="border border-gray-200 bg-white rounded-lg h-10 px-3 text-xs w-full focus:outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  </VStack>
                  <VStack className="w-64 gap-1">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                      PII Label / Category
                    </span>
                    <select
                      value={addLabel}
                      onChange={(e) => setAddLabel(e.target.value)}
                      className="border border-gray-200 bg-white rounded-lg h-10 px-3 text-xs w-full focus:outline-none focus:ring-1 focus:ring-primary/30"
                    >
                      <optgroup label="Built-in PII">
                        {Object.entries(ALLOWED_LABELS_MAP)
                          .filter(
                            ([k]) =>
                              k.startsWith("private_") ||
                              k === "account_number" ||
                              k === "secret"
                          )
                          .map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                      </optgroup>
                      <optgroup label="Company-related">
                        {Object.entries(ALLOWED_LABELS_MAP)
                          .filter(([k]) => k.startsWith("company_"))
                          .map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                      </optgroup>
                    </select>
                  </VStack>
                  <button
                    type="button"
                    disabled={loading || !addText.trim()}
                    onClick={handleAddSpan}
                    className="flex items-center justify-center gap-1.5 h-10 px-5 bg-gray-800 text-white rounded-lg text-xs font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50"
                  >
                    <Icon icon="mdi:plus" className="w-4 h-4" />
                    Add
                  </button>
                </HStack>
              </div>
            </VStack>
          )}

          {step === "review" && generatedJson && (
            <VStack className="gap-6">
              {/* Success banner */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
                <Icon
                  icon="mdi:check-circle-outline"
                  className="w-5 h-5 text-green-600 shrink-0 mt-0.5"
                />
                <VStack className="gap-1">
                  <span className="text-xs font-semibold text-green-800">
                    AI Extraction Complete
                  </span>
                  <span className="text-xs text-green-700">
                    Review what was extracted from your document below. Once
                    you&apos;re happy, click &quot;Apply These Settings&quot; to
                    apply them to the plan.
                  </span>
                </VStack>
              </div>

              {/* Source document */}
              <VStack className="gap-1.5">
                <span className="text-xs font-semibold text-gray-700">
                  Source Document
                </span>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 space-y-0.5">
                  <p className="font-medium text-gray-800">
                    {fileInput ? `📄 ${fileInput.name}` : "📝 Pasted Text"}
                  </p>
                  <p>
                    Text was scanned for PII, redacted, then sent to the AI
                    model for analysis.
                  </p>
                </div>
              </VStack>

              {/* Redacted text — collapsible */}
              <details className="rounded-lg border border-gray-200 overflow-hidden text-xs">
                <summary className="px-4 py-3 bg-gray-50 cursor-pointer font-semibold text-gray-700 hover:bg-gray-100 select-none">
                  View Redacted Text Sent to AI
                </summary>
                <div className="p-4 font-mono text-gray-500 whitespace-pre-wrap max-h-36 overflow-y-auto bg-white">
                  {redactResult?.redacted_text}
                </div>
              </details>

              {/* Active employee expiry */}
              <VStack className="gap-1.5">
                <span className="text-xs font-semibold text-gray-700">
                  Active Employee Options
                </span>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-700">
                  {fmtActiveExpiry(generatedJson.activeEmployee)}
                </div>
              </VStack>

              {/* Exit rules */}
              <VStack className="gap-3">
                <HStack className="items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">
                    Exit Rules
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                    {generatedJson.exitRules.length} extracted
                  </span>
                </HStack>
                {generatedJson.exitRules.map((rule, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm"
                  >
                    {/* Card header */}
                    <div className="flex items-center gap-2.5 px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0">
                        <Icon
                          icon={exitTypeIcon(rule.exitType)}
                          className="w-3.5 h-3.5 text-gray-500"
                        />
                      </div>
                      <span className="text-xs font-bold text-gray-800 tracking-wide">
                        {fmtExitType(rule.exitType)}
                      </span>
                    </div>

                    {/* Vested / Unvested stat row */}
                    <div className="grid grid-cols-2 divide-x divide-gray-100 px-0">
                      <div className="px-4 py-3">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-1">
                          Vested Options
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Icon
                            icon="mdi:clock-outline"
                            className="w-3.5 h-3.5 text-green-500 shrink-0"
                          />
                          <span className="text-xs font-medium text-gray-700">
                            {fmtWindow(rule.vestedOptions.exerciseWindow)} to
                            exercise
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5 pl-5">
                          On expiry:{" "}
                          {rule.vestedOptions.onExpiry === "FORFEIT"
                            ? "Forfeited"
                            : "Lapsed"}
                        </p>
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-1">
                          Unvested Options
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Icon
                            icon={
                              rule.unvestedOptions.forfeitImmediately
                                ? "mdi:close-circle-outline"
                                : "mdi:timer-sand"
                            }
                            className={`w-3.5 h-3.5 shrink-0 ${
                              rule.unvestedOptions.forfeitImmediately
                                ? "text-red-400"
                                : "text-amber-500"
                            }`}
                          />
                          <span className="text-xs font-medium text-gray-700">
                            {rule.unvestedOptions.forfeitImmediately
                              ? "Forfeited immediately"
                              : `${
                                  rule.unvestedOptions.gracePeriodDays ?? 0
                                }-day grace period`}
                          </span>
                        </div>
                        {rule.unvestedOptions.acceleration?.enabled && (
                          <p className="text-[10px] text-gray-400 mt-0.5 pl-5">
                            {rule.unvestedOptions.acceleration.percentage}%
                            acceleration
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Tenure-based overrides */}
                    {!!rule.conditionalRules?.length && (
                      <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Icon
                            icon="mdi:table-clock"
                            className="w-3.5 h-3.5 text-gray-400"
                          />
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                            Tenure Overrides
                          </p>
                          <span className="ml-auto text-[10px] text-gray-400">
                            {rule.conditionalRules.length} rule
                            {rule.conditionalRules.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {rule.conditionalRules.map((cr, j) => (
                            <div
                              key={j}
                              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                            >
                              <Icon
                                icon="mdi:account-clock-outline"
                                className="w-3.5 h-3.5 text-gray-400 shrink-0"
                              />
                              <span className="text-xs font-medium text-gray-700 flex-1">
                                {cr.tenure
                                  ? fmtTenureBand(cr.tenure)
                                  : "Any tenure"}
                              </span>
                              {cr.vestedOptions && (
                                <>
                                  <Icon
                                    icon="mdi:arrow-right"
                                    className="w-3 h-3 text-gray-300 shrink-0"
                                  />
                                  <span className="text-xs text-gray-500 shrink-0">
                                    {fmtWindow(cr.vestedOptions.exerciseWindow)}{" "}
                                    window
                                  </span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </VStack>
            </VStack>
          )}

          {step === "generating" && (
            <div className="py-16 flex flex-col items-center justify-center gap-4 text-center">
              <Loader />
              <VStack className="gap-1 mt-2">
                <span className="text-sm font-semibold text-gray-800">
                  AI is analyzing and generating forfeiture settings...
                </span>
                <span className="text-xs text-gray-400 max-w-sm">
                  Sending redacted document text to LLM model. Extracting rules
                  for resignation, termination, retirement, and exercise
                  windows. Please wait.
                </span>
              </VStack>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 border-t py-4 shrink-0 bg-gray-50">
          <HStack className="justify-between items-center">
            {step === "review" ? (
              <ButtonPrimary1
                type="button"
                disabled={loading}
                onClick={() => setStep("verify")}
              >
                Back
              </ButtonPrimary1>
            ) : step === "verify" ? (
              <ButtonPrimary1
                type="button"
                disabled={loading}
                onClick={() => setStep("input")}
              >
                Back
              </ButtonPrimary1>
            ) : (
              <ButtonPrimary1
                type="button"
                disabled={loading}
                onClick={onClose}
              >
                Cancel
              </ButtonPrimary1>
            )}

            <HStack className="items-center gap-3">
              {step === "input" && (
                <ButtonPrimary
                  type="button"
                  disabled={loading}
                  onClick={handleStartAnalysis}
                >
                  {loading ? "Enqueuing..." : "Analyse Policy ✨"}
                </ButtonPrimary>
              )}
              {step === "verify" && (
                <ButtonPrimary
                  type="button"
                  disabled={loading}
                  onClick={handleConfirmAndGenerate}
                >
                  Confirm & Generate Settings ✨
                </ButtonPrimary>
              )}
              {step === "review" && generatedJson && (
                <ButtonPrimary
                  type="button"
                  onClick={() => onApply(generatedJson)}
                >
                  Apply These Settings ✨
                </ButtonPrimary>
              )}
            </HStack>
          </HStack>
        </div>
      </Box>
    </Dialog>
  );
}
